import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useLocation, useNavigate } from "react-router-dom";
import { Table, Button, Switch, Spin, message } from "antd";
import {
  ArrowRightOutlined,
  DownloadOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DeploymentUnitOutlined,
} from "@ant-design/icons";
import PageHeader from "../../components/generic/PageHeader";
import MasterSuiteFlow from "../../components/mastersuite/MasterSuiteFlow";
import ErrorBoundary from "../../components/generic/ErrorBoundary";
import { toFlowNodes } from "../../helper/masterSuiteFlow";
import { useMediaQuery } from "../../helper/useMediaQuery";
import { navigateToStepResult } from "../../helper/stepResultNavigation";
import { isNotRun } from "../../helper/run_status";
import {
  getMasterSuiteDetailResultByJob,
  getResultSummaryByKey,
} from "../../service/result.service";
import { exportToCsv } from "../../helper/export_to_csv";
import "../../styles/results.css";
import {
  StatsStrip,
  MiniBar,
  StatusBadge,
} from "../../components/results/ResultStats";

// ─── Shared style tokens ─────────────────────────────────────
const S = {
  passBg: "#EAF3DE",
  passText: "#3B6D11",
  failBg: "#FCEBEB",
  failText: "#A32D2D",
  blueBg: "#E6F1FB",
  blueText: "#185FA5",
  ambBg: "#FAEEDA",
  ambText: "#854F0B",
  grayBg: "#F1EFE8",
  grayText: "#5F5E5A",
  border: "0.5px solid var(--color-border-tertiary, #e8e8e8)",
  radius: 8,
  radiusLg: 10,
};

// ─── Category badge ───────────────────────────────────────────
function CatBadge({ type }) {
  const isCompare = type?.toLowerCase() === "compare";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 500,
        background: isCompare ? S.blueBg : S.grayBg,
        color: isCompare ? S.blueText : S.grayText,
      }}
    >
      {type || "—"}
    </span>
  );
}

// ─── Step breakdown donut chart ──────────────────────────────
function StepPieChart({ passed, failed, stopped }) {
  const segments = [
    { label: "Passed",  value: passed,  color: "#3B6D11" },
    { label: "Failed",  value: failed,  color: "#A32D2D" },
    // Stopped + Skipped + Timed Out share one slice — from the reader's point of view
    // they're the same thing: the step produced no result.
    { label: "Did not run", value: stopped, color: "#5F5E5A" },
  ].filter((d) => d.value > 0);

  const total = passed + failed + stopped;

  if (segments.length === 0) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 80, color: "#bbb", fontSize: 12 }}>
        No data
      </div>
    );
  }

  const options = {
    chart: { type: "donut", fontFamily: "inherit", toolbar: { show: false } },
    labels: segments.map((d) => d.label),
    colors: segments.map((d) => d.color),
    legend: { position: "bottom", fontSize: "12px", markers: { size: 8, shape: "square" } },
    plotOptions: {
      pie: {
        donut: {
          size: "68%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total",
              fontSize: "11px",
              color: "#888",
              formatter: () => total,
            },
            value: { fontSize: "24px", fontWeight: 700, color: "#0B1F3A", offsetY: 4 },
            name: { offsetY: -4 },
          },
        },
      },
    },
    dataLabels: { enabled: true, formatter: (v) => `${Math.round(v)}%`, style: { fontSize: "11px" }, dropShadow: { enabled: false } },
    stroke: { width: 2 },
    tooltip: { y: { formatter: (v) => `${v} steps` } },
  };

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <ReactApexChart
        type="donut"
        options={options}
        series={segments.map((d) => d.value)}
        height={230}
        width={280}
      />
    </div>
  );
}

// ─── Meta row ─────────────────────────────────────────────────
function MetaRow({ label, children }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "6px 0",
        borderBottom: S.border,
        gap: 8,
        fontSize: 13,
      }}
    >
      <span
        style={{
          color: "var(--color-text-secondary, #888)",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontWeight: 500,
          textAlign: "right",
          wordBreak: "break-word",
          maxWidth: "65%",
        }}
      >
        {children}
      </span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────
function MasterSuiteResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const suiteJobId = location.state?.key;

  const [steps, setSteps] = useState([]);
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);

  // Below 1024px show a compact vertical step list instead of the canvas —
  // "did the run pass" shouldn't require horizontal scrolling on a phone.
  const isNarrow = useMediaQuery("(max-width: 1024px)");

  useEffect(() => {
    if (!suiteJobId) return;

    getMasterSuiteDetailResultByJob(suiteJobId)
      .then((res) => {
        setSteps(res.data || []);
        setLoading(false);
      })
      .catch(() => {
        message.error("Error fetching master suite step details");
        setLoading(false);
      });

    getResultSummaryByKey(suiteJobId)
      .then((res) => {
        setJobData(res.data);
        setPageLoading(false);
      })
      .catch(() => setPageLoading(false));
  }, [suiteJobId]);

  const clickStep = (record) => navigateToStepResult(navigate, record);

  if (pageLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  // ── Derived stats ───────────────────────────────────────────
  const totalSteps = steps.length;
  const passedSteps = steps.filter((s) => s.stepTestResult === "Pass").length;
  // A step that never ran (Stopped / Skipped / Timed Out) is NOT a failure — counting it
  // as one would blame a branch for its upstream's fault.
  const failedSteps = steps.filter(
    (s) => (s.stepTestResult === "Fail" || !s.stepTestResult) && !isNotRun(s.stepTestResult),
  ).length;
  const stoppedSteps = steps.filter((s) => isNotRun(s.stepTestResult)).length;
  const passRate =
    totalSteps > 0 ? Math.round((passedSteps / totalSteps) * 100) : 0;

  const suiteName =
    steps[0]?.masterSuiteName || jobData?.inputName || "Master Suite";
  const suiteStatus =
    steps[0]?.masterSuiteRunStatus || jobData?.testResult || "—";
  const overallPass = suiteStatus === "Pass";

  // ── Step table columns ──────────────────────────────────────
  const columns = [
    {
      title: "#",
      dataIndex: "stepSequenceNumber",
      key: "stepSequenceNumber",
      width: 52,
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.stepSequenceNumber - b.stepSequenceNumber,
      render: (v, record) => (
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background:
              record.stepTestResult === "Pass"
                ? S.passBg
                : isNotRun(record.stepTestResult)
                  ? S.grayBg
                  : S.failBg,
            color:
              record.stepTestResult === "Pass"
                ? S.passText
                : isNotRun(record.stepTestResult)
                  ? S.grayText
                  : S.failText,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {v}
        </div>
      ),
    },
    {
      title: "Step",
      dataIndex: "stepName",
      key: "stepName",
      render: (v) => (
        <span style={{ fontWeight: 500, fontSize: 13 }}>{v || "—"}</span>
      ),
    },
    {
      title: "Task",
      dataIndex: "taskName",
      key: "taskName",
      render: (text, record) =>
        record.stepResultId ? (
          <a
            style={{ color: S.blueText, fontSize: 13 }}
            onClick={(e) => {
              e.preventDefault();
              clickStep(record);
            }}
            href="#"
          >
            {text}
            {(record.subCategory || "").toLowerCase() === "ui test" ? (
              <span
                style={{
                  fontSize: 10,
                  marginLeft: 6,
                  background: "#E6F1FB",
                  color: S.blueText,
                  padding: "1px 5px",
                  borderRadius: 3,
                }}
              >
                UI Report
              </span>
            ) : (
              <ArrowRightOutlined style={{ marginLeft: 4, fontSize: 10 }} />
            )}
          </a>
        ) : (
          <span style={{ fontSize: 13, color: "#888" }}>{text || "—"}</span>
        ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 110,
      render: (v) => <CatBadge type={v} />,
    },
    {
      title: "Sub category",
      dataIndex: "subCategory",
      key: "subCategory",
      width: 130,
      render: (v) => (
        <span style={{ fontSize: 12, color: "#666" }}>{v || "—"}</span>
      ),
    },
    {
      title: "Scenarios",
      dataIndex: "totalTestCount",
      key: "totalTestCount",
      width: 90,
      align: "right",
    },
    {
      title: "Pass",
      dataIndex: "passedTestCount",
      key: "passedTestCount",
      width: 60,
      align: "right",
      render: (v) => (
        <span
          style={{
            color: v > 0 ? S.passText : "#bbb",
            fontWeight: v > 0 ? 500 : 400,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: "Fail",
      dataIndex: "failedTestCount",
      key: "failedTestCount",
      width: 60,
      align: "right",
      render: (v) => (
        <span
          style={{
            color: v > 0 ? S.failText : "#bbb",
            fontWeight: v > 0 ? 600 : 400,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: "Pass rate",
      key: "passRate",
      width: 120,
      render: (_, r) => (
        <MiniBar pass={r.passedTestCount} total={r.totalTestCount} />
      ),
    },
    {
      title: "Stop if failed",
      dataIndex: "stopIfFailed",
      key: "stopIfFailed",
      width: 100,
      align: "center",
      render: (v) => (
        <Switch
          checked={v}
          checkedChildren="ON"
          unCheckedChildren="OFF"
          size="small"
          disabled
          style={{ opacity: 1 }}
        />
      ),
    },
    {
      title: "Run start",
      dataIndex: "stepRunStart",
      key: "stepRunStart",
      width: 140,
      render: (v) => (
        <span style={{ fontSize: 12, color: "#888" }}>{v || "—"}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "stepTestResult",
      key: "stepTestResult",
      width: 110,
      align: "center",
      filters: [
        { text: "Passed", value: "Pass" },
        { text: "Failed", value: "Fail" },
        { text: "Partial", value: "Partial" },
        { text: "Skipped", value: "Skipped" },
        { text: "Stopped", value: "Stopped" },
        { text: "Timed Out", value: "Timed Out" },
        { text: "Error", value: "Error" },
      ],
      onFilter: (v, r) => (r.stepTestResult || "Fail") === v,
      render: (v) => <StatusBadge status={v} />,
    },
  ];

  return (
    <div style={{ padding: "16px 24px" }}>
      {/* ── Header ─────────────────────────────────────────── */}
      <PageHeader
        backTo={-1}
        icon={<DeploymentUnitOutlined />}
        title={suiteName}
        subtitle={
          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              fontSize: 12,
              color: "var(--color-text-secondary, #888)",
            }}
          >
            {jobData?.user && (
              <span>
                <UserOutlined style={{ marginRight: 4 }} />
                {jobData.user}
              </span>
            )}
            {jobData?.runStart && (
              <span>
                <CalendarOutlined style={{ marginRight: 4 }} />
                Started {jobData.runStart}
              </span>
            )}
            {jobData?.runEnd && (
              <span>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                Ended {jobData.runEnd}
              </span>
            )}
            <span style={{ fontFamily: "monospace", fontSize: 11 }}>
              {suiteJobId}
            </span>
          </div>
        }
        actions={
          <>
            <StatusBadge status={suiteStatus} />
            <Button
              icon={<DownloadOutlined />}
              onClick={() => exportToCsv("MasterSuiteResultDetail.csv", steps)}
            >
              Export
            </Button>
          </>
        }
      />

      {/* ── Stats strip ────────────────────────────────────── */}
      <StatsStrip
        items={[
          { label: "Total steps", value: totalSteps, color: "#93C5FD" },
          { label: "Passed", value: passedSteps, color: "#86EFAC" },
          { label: "Failed", value: failedSteps, color: failedSteps > 0 ? "#FCA5A5" : "rgba(255,255,255,0.35)" },
          { label: "Did not run", value: stoppedSteps, color: stoppedSteps > 0 ? "#FCD34D" : "rgba(255,255,255,0.35)" },
        ]}
        segments={[
          { value: passedSteps,  color: "#86EFAC" },
          { value: failedSteps,  color: "#FCA5A5" },
          { value: stoppedSteps, color: "rgba(255,255,255,0.35)" },
        ]}
        passRateLabel="Step pass rate"
      />

      {/* ── Suite info + Pie chart ──────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 20,
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            background: "var(--color-background-secondary, #f5f5f5)",
            borderRadius: S.radiusLg,
            padding: "14px 16px",
            minWidth: 320,
            maxWidth: 400,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: "var(--color-text-secondary, #888)",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 10,
            }}
          >
            Suite details
          </div>
          <MetaRow label="Suite name">{suiteName}</MetaRow>
          <MetaRow label="Run by">{jobData?.user || "—"}</MetaRow>
          <MetaRow label="Started">{jobData?.runStart || "—"}</MetaRow>
          <MetaRow label="Ended">{jobData?.runEnd || "—"}</MetaRow>
          <MetaRow label="Environment">
            {jobData?.suiteEnvironment || "—"}
          </MetaRow>
          <MetaRow label="Job status">{jobData?.jobStatus || "—"}</MetaRow>
        </div>
        <div
          style={{
            background: "var(--color-background-secondary, #f5f5f5)",
            borderRadius: S.radiusLg,
            padding: "14px 16px",
            flex: 1,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: "var(--color-text-secondary, #888)",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 4,
            }}
          >
            Step breakdown
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--color-text-secondary, #888)",
              marginBottom: 8,
            }}
          >
            Pass / fail / did not run, per step
          </div>
          <StepPieChart
            passed={passedSteps}
            failed={failedSteps}
            stopped={stoppedSteps}
          />
        </div>
      </div>

      {/* ── Workflow (visual flow of the run) ──────────────── */}
      {isNarrow ? (
        <div className="ms-steplist">
          {[...steps]
            .sort((a, b) => a.stepSequenceNumber - b.stepSequenceNumber)
            .map((s) => {
              const st =
                s.stepTestResult === "Pass"
                  ? "pass"
                  : isNotRun(s.stepTestResult)
                    ? "stopped"
                    : "fail";
              return (
                <div
                  key={s.stepResultId || s.stepSequenceNumber}
                  className={`ms-steplist-item ms-steplist-item--${st}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => clickStep(s)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") clickStep(s);
                  }}
                >
                  <span className="ms-steplist-seq">{s.stepSequenceNumber}</span>
                  <span className="ms-steplist-main">
                    <span className="ms-steplist-name">
                      {s.stepName || "Untitled step"}
                    </span>
                    <span className="ms-steplist-meta">
                      {(s.taskName || s.subCategory || "—") +
                        (s.stepRunStart ? ` · ${s.stepRunStart}` : "") +
                        (s.totalTestCount != null
                          ? ` · ${s.passedTestCount || 0}/${s.totalTestCount} passed`
                          : "")}
                    </span>
                  </span>
                  <span className={`ms-steplist-status ms-steplist-status--${st}`}>
                    {s.stepTestResult || "Fail"}
                  </span>
                </div>
              );
            })}
        </div>
      ) : (
        // Same layout code as the builder, so the same containment: a diagram that fails
        // to draw must not take the results table down with it.
        <ErrorBoundary
          title="The workflow diagram couldn't be drawn"
          hint="The step results below are unaffected."
        >
          <MasterSuiteFlow
            nodes={toFlowNodes(steps, "result")}
            mode="result"
            onNodeClick={(node) => clickStep(node.raw)}
            title="Workflow"
            subtitle="Click a step to view its result"
          />
        </ErrorBoundary>
      )}

      {/* ── Steps table ────────────────────────────────────── */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div>
            <span style={{ fontSize: 14, fontWeight: 500 }}>Step results</span>
            <span style={{ fontSize: 12, color: "#888", marginLeft: 10 }}>
              {totalSteps} steps · {passedSteps} passed · {failedSteps} failed
              {stoppedSteps > 0 && ` · ${stoppedSteps} did not run`}
            </span>
          </div>
          <span style={{ fontSize: 12, color: "#888" }}>
            Click a task name to view its full result detail
          </span>
        </div>
        <div
          style={{
            background: "#fff",
            border: S.border,
            borderRadius: S.radiusLg,
            overflow: "hidden",
          }}
        >
          <Table
            columns={columns}
            dataSource={steps}
            rowKey={(r) => r.stepResultId || r.stepSequenceNumber}
            loading={loading}
            size="middle"
            scroll={{ x: 1100 }}
            rowClassName={(r) =>
              isNotRun(r.stepTestResult)
                ? "arc-stopped-row"
                : r.stepTestResult === "Fail" || !r.stepTestResult
                  ? "arc-fail-row"
                  : ""
            }
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
            }}
            style={{ fontSize: 13 }}
          />
        </div>
      </div>

      <style>{`
        .arc-fail-row td    { background: #fff9f9 !important; }
        .arc-fail-row:hover td    { background: #fff2f2 !important; }
        .arc-stopped-row td { background: #fafafa !important; }
        .arc-stopped-row:hover td { background: #f5f5f5 !important; }
      `}</style>
    </div>
  );
}

export default MasterSuiteResults;
